/* eslint-disable @surglogs/no-cross-imports */
import { indexBy, prop } from 'ramda'
import {
  CustomProps,
  getMountedApps,
  registerApplication,
  RegisterApplicationConfig,
  start,
} from 'single-spa'

import { isFunction } from '@surglogs/util.is-function'

import {
  POLICIES_ROOT_ROUTE,
  ACCREDITATION_ROOT_ROUTE,
  LOGGING_ROOT_ROUTE,
  SETTINGS_ROOT_ROUTE,
  DASHBOARD_ROOT_ROUTE,
  TRACKING_ROOT_ROUTE,
  DOCUMENTS_ROOT_ROUTE,
  DOCUMENTS_USER_DOC_SIGN_ROUTE,
  EXPORTS_ROUTE,
  AUTH_LOGIN_ROUTE,
  CLINICS_ROUTE,
  UNAUTHORIZED_ROUTE,
  NO_ACCESS_ROUTE,
  CONTRACTS_ROOT_ROUTE,
  DOCUMENTS_FILLABLE_FORMS_ROOT_ROUTE,
  PAYMENT_REQUIRED_ROUTE,
  PERMISSION_REQUIRED_ROUTE,
  NOT_FOUND_ROUTE,
  DEVICE_ONLY_ROUTE,
  USER_DEACTIVATED_ROUTE,
  AUTH_LOGOUT_ROUTE,
  LOGGING_LEGACY_ROOT_ROUTE,
  REPORTS_ROOT_ROUTE,
  MOBILE_APP_ROUTE,
  CLINIC_AUDITS_ROOT_ROUTE,
} from '@surglogs-app/routing'
import { BootstrapHook } from '@surglogs-app/types'

import {
  APPLE_STORE_APP_LINK,
  GOOGLE_STORE_APP_LINK,
} from '../../constants/AppStoreLink'

// TODO: any should not be used for props here
interface AppEntity<T extends CustomProps = any>
  extends RegisterApplicationConfig<T> {
  fullFledged: boolean
  onlyIf?: () => boolean
}

export const initApps: BootstrapHook = (
  config,
  {
    history,
    auth,
    featureFlagsInstance,
    queryClient,
    clinicContextStoreInstance,
  },
) => {
  const RootApp: AppEntity = {
    name: 'root',
    app: () => import('../../App'),
    activeWhen: [
      EXPORTS_ROUTE,
      AUTH_LOGIN_ROUTE,
      CLINICS_ROUTE, // TODO: do we need this prefix?
      UNAUTHORIZED_ROUTE,
      NO_ACCESS_ROUTE,
      PAYMENT_REQUIRED_ROUTE,
      MOBILE_APP_ROUTE,
      PERMISSION_REQUIRED_ROUTE,
    ],
    customProps: {
      history,
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    fullFledged: true,
  }

  const NotFoundApp: AppEntity = {
    name: 'notFound',
    activeWhen: NOT_FOUND_ROUTE,
    fullFledged: true,
    app: () => import('../../apps/notFound/App'),
    customProps: {
      history,
      auth,
      config,
      featureFlagsInstance,
      clinicContextStoreInstance,
    },
  }

  const OnlyDeviceApp: AppEntity = {
    name: 'onlyDeviceError',
    activeWhen: DEVICE_ONLY_ROUTE,
    fullFledged: true,
    app: () => import('../../apps/onlyDeviceError/App'),
    customProps: {
      auth,
      featureFlagsInstance,
      queryClient,
      config,
      clinicContextStoreInstance,
    },
  }

  const UserDeactivatedApp: AppEntity = {
    name: 'userDeactivated',
    activeWhen: USER_DEACTIVATED_ROUTE,
    fullFledged: true,
    app: () => import('../../apps/userDeactivated/App'),
    customProps: {
      auth,
      featureFlagsInstance,
      queryClient,
      config,
      clinicContextStoreInstance,
    },
  }

  const RootRedirectApp: AppEntity = {
    name: 'redirect',
    activeWhen: ({ pathname }) => pathname === '/',
    fullFledged: true,
    app: () => import('../../apps/redirect/App'),
    customProps: { history, auth, clinicContextStoreInstance },
  }

  const MobileBarApp: AppEntity = {
    name: 'mobileBar',
    activeWhen: '/',
    fullFledged: false,
    customProps: {
      APPLE_STORE_APP_LINK,
      GOOGLE_STORE_APP_LINK,
    },
    app: () => import('../../apps/mobilebar/App'),
  }

  const GlobalApp: AppEntity = {
    name: 'global',
    activeWhen: '/',
    fullFledged: false,
    app: () => import('../../apps/global/App'),
  }

  const AlertsApp: AppEntity = {
    name: 'alerts',
    activeWhen: '/',
    fullFledged: false,
    app: () => import('../../apps/alerts/App'),
  }

  const TrackingApp: AppEntity = {
    name: 'tracking',
    activeWhen: [TRACKING_ROOT_ROUTE],
    fullFledged: true,
    customProps: {
      history,
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    app: () => import('../../../../tracking/src/App' as any),
  }

  const DashboardApp: AppEntity = {
    name: 'dashboard',
    activeWhen: [DASHBOARD_ROOT_ROUTE],
    fullFledged: true,
    customProps: {
      history,
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    app: () => import('../../../../dashboard/src/App' as any),
  }

  const SettingsApp: AppEntity = {
    name: 'settings',
    activeWhen: [SETTINGS_ROOT_ROUTE],
    fullFledged: true,
    customProps: {
      history,
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    app: () => import('../../../../settings/src/App' as any),
  }

  const SurgicalLogsApp: AppEntity = {
    name: 'surgicallogs',
    activeWhen: [LOGGING_ROOT_ROUTE, LOGGING_LEGACY_ROOT_ROUTE],
    fullFledged: true,
    customProps: {
      history,
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    app: () => import('../../../../logging/src/App'),
  }

  const AccountApp: AppEntity = {
    name: 'account',
    activeWhen: ({ search }) => search.includes('subApp=account'),
    fullFledged: true,
    app: () => import('../../apps/account/App'),
    customProps: {
      history,
      auth,
      queryClient,
      featureFlagsInstance,
      clinicContextStoreInstance,
    },
  }

  const IntercomApp: AppEntity = {
    name: 'intercom',
    activeWhen: '/',
    fullFledged: false,
    customProps: { auth, config, queryClient, clinicContextStoreInstance },
    app: () => import('../../apps/intercom/App'),
  }

  const SentryApp: AppEntity = {
    name: 'sentry',
    activeWhen: '/',
    fullFledged: false,
    customProps: { auth, config, history, clinicContextStoreInstance },
    app: () => import('../../apps/sentry/App'),
  }

  const UpdateApp: AppEntity = {
    name: 'updater',
    activeWhen: '/',
    fullFledged: false,
    customProps: { auth, config, queryClient, clinicContextStoreInstance },
    app: () => import('../../apps/updater/App'),
  }

  const LogoutApp: AppEntity = {
    name: 'logout',
    activeWhen: AUTH_LOGOUT_ROUTE,
    fullFledged: true,
    customProps: { auth, queryClient, clinicContextStoreInstance },
    app: () => import('../../apps/logout/App'),
  }

  const SidebarApp: AppEntity = {
    name: 'sidebar',
    activeWhen: ({ pathname }) => pathname !== '/',
    /**
     * sidebar has to be always "rendered" here,
     * otherwise this component would append to bottom of the dom,
     * and therefore create bottom white margin */
    fullFledged: false,
    customProps: {
      auth,
      config,
      queryClient,
      featureFlagsInstance,
      clinicContextStoreInstance,
    },
    app: () => import('../../apps/sidebar/App'),
  }

  const AccreditationApp: AppEntity = {
    name: 'accreditation',
    activeWhen: [ACCREDITATION_ROOT_ROUTE],
    fullFledged: true,
    customProps: {
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    app: () => import('../../../../accreditation/src/App' as any),
  }

  const PoliciesApp: AppEntity = {
    name: 'policies',
    activeWhen: [POLICIES_ROOT_ROUTE],
    fullFledged: true,
    customProps: {
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    app: () => import('../../../../policies/src/App' as any),
  }

  const DocumentsApp: AppEntity = {
    name: 'documents',
    activeWhen: [
      DOCUMENTS_ROOT_ROUTE,
      DOCUMENTS_USER_DOC_SIGN_ROUTE,
      DOCUMENTS_FILLABLE_FORMS_ROOT_ROUTE,
    ],
    fullFledged: true,
    customProps: {
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    app: () => import('../../../../documents/src/App' as any),
  }

  const ContractsApp: AppEntity = {
    name: 'contracts',
    activeWhen: [CONTRACTS_ROOT_ROUTE],
    fullFledged: true,
    customProps: {
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    app: () => import('../../../../contracts/src/App' as any),
  }

  const ReportsApp: AppEntity = {
    name: 'reports',
    activeWhen: [REPORTS_ROOT_ROUTE],
    fullFledged: true,
    customProps: {
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    app: () => import('../../../../reports/src/App' as any),
  }

  const ClinicAuditsApp: AppEntity = {
    name: 'clinic-audits',
    activeWhen: [CLINIC_AUDITS_ROOT_ROUTE],
    fullFledged: true,
    customProps: {
      auth,
      config,
      featureFlagsInstance,
      queryClient,
      clinicContextStoreInstance,
    },
    app: () => import('../../../../clinic-audits/src/App' as any),
  }

  /**
   * @NOTE
   *
   * There is specific order needed, we need optimizely initialized as first,
   * othwerwise would could get situation where some other app will call FF decision hook
   * and optimizely client will not be ready yet
   */
  const apps: AppEntity[] = [
    SentryApp,
    SidebarApp,
    AccountApp,
    AlertsApp,
    GlobalApp,
    RootApp,
    NotFoundApp,
    OnlyDeviceApp,
    UserDeactivatedApp,
    RootRedirectApp,
    MobileBarApp,
    SurgicalLogsApp,
    TrackingApp,
    IntercomApp,
    UpdateApp,
    LogoutApp,
    AccreditationApp,
    PoliciesApp,
    SettingsApp,
    DashboardApp,
    DocumentsApp,
    ContractsApp,
    ReportsApp,
    ClinicAuditsApp,
  ]

  apps
    .filter(({ onlyIf }) => !onlyIf || (isFunction(onlyIf) && onlyIf()))
    .forEach(({ name, app, activeWhen, customProps }) =>
      registerApplication({ name, app, activeWhen, customProps }),
    )

  const appsHash = indexBy<AppEntity>(prop('name'))(apps)

  const filterOnlyFullFledgedApps = (name: string) => {
    const a = appsHash[name]
    return a && a.fullFledged
  }

  window.addEventListener('single-spa:routing-event', () => {
    const mountedApps = getMountedApps()
    const fullFledgedApps = mountedApps.filter(filterOnlyFullFledgedApps)
    const notAlreadyOn404Route =
      window.location.pathname !== NotFoundApp.activeWhen
    if (!fullFledgedApps.length && notAlreadyOn404Route) {
      // try {
      //   // eslint-disable-next-line
      //   console.error('Location not found', e.detail.newUrl)
      //   // eslint-disable-next-line no-empty
      // } catch (error) {}

      history.replace(NotFoundApp.activeWhen as string)
    }
  })

  start()
}
